import {
  ITextNodeSchema,
  initVersionedSchema,
  toTextContent,
} from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  ClinicalNote,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  ITreatmentConfiguration,
  type IClinicalNote,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  ISODateType,
  Timestamp,
  Timezone,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  sortTimestamp,
  toISODate,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { groupBy, isNull } from 'lodash';
import { Observable, combineLatest, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalNoteDestinationEntity,
  IClinicalNoteJobData,
  IClinicalNoteMigrationData,
} from '../../../destination/entities/patient-clinical-notes';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { isClinicalNoteCode } from '../../../mappings/item-code-mapping-helpers';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { ADAItemSourceEntity } from '../../source/entities/ada-item';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import {
  ExactBPEScore,
  PATIENT_BPE_RESOURCE_TYPE,
  PatientBPESourceEntity,
} from '../../source/entities/patient-bpe-scores';
import {
  ExactPathologyNote,
  PATIENT_PATHOLOGY_NOTE_RESOURCE_TYPE,
  PatientPathologyNotesSourceEntity,
} from '../../source/entities/patient-pathology-notes';
import {
  ExactTreatment,
  PatientTreatmentSourceEntity,
} from '../../source/entities/patient-treatments';
import { ExactItemCodeToNoteMappingHandler } from '../mappings/item-code-to-notes';
import { ExactItemCodeMappingHandler } from '../mappings/item-codes';
import {
  ExactStafferMappingHandler,
  resolveExactStaffer,
} from '../mappings/staff';
import { PatientDestinationEntity } from './patient';
import { ExactItemCodeToTreatmentMappingHandler } from '../mappings/item-code-to-treatment';
import { ItemCodeResourceMapType } from '../../../mappings/item-codes-to-xlsx';
import { resolveMappedCodeTreatmentPair } from '../../../mappings/item-codes';

export interface IExactClinicalNoteJobData
  extends IClinicalNoteJobData<ExactPatient> {
  itemCodesToNotes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
  itemCodeToTreatments: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[];
}

export const EXACT_CLINICAL_NOTE_DESCRIPTION = `
Clinical Notes are stored in Exact's treatment table so we must retrieve all treatments that contain notes and based on a combination of the user id and treatment date
we will create a clinical note for that user on that date.

Exact replicates the same clinical note where treatments are subsqeuent codes of the same treatment. We will reduce this sequence based on matching strings to create a single clinical note.
`;

export class PatientClinicalNoteDestinationEntity extends BasePatientClinicalNoteDestinationEntity<ExactPatient> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
    bpeScores: new PatientBPESourceEntity(),
    pathologyNotes: new PatientPathologyNotesSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    itemCodes: new ExactItemCodeMappingHandler(),
    itemCodeToTreatment: new ExactItemCodeToTreatmentMappingHandler(),
    itemCodesToNotes: new ExactItemCodeToNoteMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IExactClinicalNoteJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const itemCodesToNotes$ =
      this.customMappings.itemCodesToNotes.getRecords(translationMap);
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const itemCodeToTreatments$ =
      this.customMappings.itemCodeToTreatment.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      sourceItemCodes$,
      itemCodesToNotes$,
      itemCodeToTreatments$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            practitioners,
            sourceItemCodes,
            itemCodesToNotes,
            itemCodeToTreatments,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            staff,
            practitioners,
            sourceItemCodes,
            itemCodesToNotes,
            sourcePatient,
            destinationEntity,
            itemCodeToTreatments,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IExactClinicalNoteJobData
  ): Promise<
    | IClinicalNoteMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const patientUid = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const clinicalNoteTreatments = await this.getClinicalNoteTreatments(
        migration,
        patientUid,
        data
      );
      const bpeScores = await this._getBpeScores(migration, patientUid);
      const pathologyNotes = await this._getPathologyNotes(
        migration,
        patientUid
      );
      const clinicalNotes = await this._buildClinicalNotesData(
        clinicalNoteTreatments,
        bpeScores,
        pathologyNotes,
        migration.configuration.timezone,
        data,
        translationMap
      );

      return {
        sourcePatientId,
        patientRef,
        clinicalNotes,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async getClinicalNoteTreatments(
    migration: WithRef<IPracticeMigration>,
    patientUid: string,
    data: IExactClinicalNoteJobData
  ): Promise<IGetRecordResponse<ExactTreatment>[]> {
    const timezone = migration.configuration.timezone;
    const clinicalNoteTreatments =
      await this.sourceEntities.treatments.filterRecords(
        migration,
        'patientId',
        patientUid,
        (record) =>
          !isNull(record.data.treatment_notes) &&
          record.data.treatment_notes !== record.data.service_description
      );

    const sourceEntity = new ADAItemSourceEntity();
    return clinicalNoteTreatments
      .filter((record) => {
        const sourceId = sourceEntity.getSourceRecordId({
          itemCode: record.data.service_code,
        });
        return isClinicalNoteCode(
          record.data.service_code,
          sourceId,
          data.sourceItemCodes,
          data.itemCodesToNotes
        );
      })
      .sort((treatmentA, treatmentB) =>
        sortTimestamp(
          this.getNoteTimestamp(treatmentA, timezone),
          this.getNoteTimestamp(treatmentB, timezone)
        )
      );
  }

  getNoteDate(
    sourceNote: IGetRecordResponse<ExactTreatment>
  ): ISODateType | undefined {
    return (
      sourceNote.record.translations.completedDate ??
      sourceNote.record.translations.plannedDate
    );
  }

  getNoteTimestamp(
    sourceNote: IGetRecordResponse<ExactTreatment>,
    timezone: Timezone
  ): Timestamp {
    const noteDate = this.getNoteDate(sourceNote);
    return noteDate
      ? toTimestamp(toMomentTz(noteDate, timezone))
      : toTimestamp();
  }

  private async _getBpeScores(
    migration: WithRef<IPracticeMigration>,
    patientUid: string
  ): Promise<IGetRecordResponse<ExactBPEScore>[]> {
    const timezone = migration.configuration.timezone;
    const patientBpeScores = await this.sourceEntities.bpeScores.filterRecords(
      migration,
      'patientId',
      patientUid
    );
    if (!patientBpeScores.length) {
      return [];
    }

    return patientBpeScores.sort((scoreA, scoreB) =>
      sortTimestamp(
        toTimestamp(toMomentTz(scoreA.record.translations.date, timezone)),
        toTimestamp(toMomentTz(scoreB.record.translations.date, timezone))
      )
    );
  }

  private async _getPathologyNotes(
    migration: WithRef<IPracticeMigration>,
    patientUid: string
  ): Promise<IGetRecordResponse<ExactPathologyNote>[]> {
    const pathologyNotes =
      await this.sourceEntities.pathologyNotes.filterRecords(
        migration,
        'patientId',
        patientUid
      );
    if (!pathologyNotes.length) {
      return [];
    }

    return pathologyNotes.sort((noteA, noteB) =>
      sortTimestamp(
        noteA.record.translations.date,
        noteB.record.translations.date
      )
    );
  }

  private _treatmentsToNoteContent(
    notes: IGetRecordResponse<ExactTreatment>[],
    sourceItemCodes: WithPrimaryKey<
      ITranslationMap<object, ItemCodeResourceMapType>
    >[],
    itemCodeToTreatments: WithPrimaryKey<
      ITranslationMap<ITreatmentConfiguration>
    >[]
  ): ITextNodeSchema[] {
    return notes
      .reduce((results: IGetRecordResponse<ExactTreatment>[], note) => {
        const existing = results.find(
          (result) => result.data.treatment_notes === note.data.treatment_notes
        );
        if (!existing) {
          results.push(note);
        }
        return results;
      }, [])
      .map((note) => [
        toTextContent(
          this._getNotePrefix(note, sourceItemCodes, itemCodeToTreatments)
        ),
        toTextContent(`${note.data.treatment_notes ?? ''}\n\n`),
      ])
      .flat();
  }

  private _getNotePrefix(
    note: IGetRecordResponse<ExactTreatment>,
    sourceItemCodes: WithPrimaryKey<
      ITranslationMap<object, ItemCodeResourceMapType>
    >[],
    itemCodeToTreatments: WithPrimaryKey<
      ITranslationMap<ITreatmentConfiguration>
    >[]
  ): string {
    const [code, mappedTreatment] = resolveMappedCodeTreatmentPair(
      sourceItemCodes,
      itemCodeToTreatments,
      note.data.service_code,
      note.data.service_code
    );
    const hasMapping = !!code || !!mappedTreatment;
    const isPlanned = !note.record.translations.completedDate;
    return `${hasMapping && isPlanned ? '(Planned)' : ''}${
      note.data.service_code
    } ${note.data.tooth ?? ''} ${
      note.data.tooth_surfaces.length ? note.data.tooth_surfaces.join(', ') : ''
    }`;
  }

  private async _buildClinicalNotesData(
    clinicalNoteTreatments: IGetRecordResponse<ExactTreatment>[],
    bpeScores: IGetRecordResponse<ExactBPEScore>[],
    pathologyNotes: IGetRecordResponse<ExactPathologyNote>[],
    timezone: Timezone,
    data: IExactClinicalNoteJobData,
    translationMap: TranslationMapHandler
  ): Promise<(IClinicalNote & IHasSourceIdentifier)[]> {
    const clinicalNotes: (IClinicalNote & IHasSourceIdentifier)[] = [];

    const userGroupedNoteTreatments = groupBy(
      clinicalNoteTreatments,
      (record) => record.data.provider_code ?? '0'
    );

    await resolveSequentially(
      Object.entries(userGroupedNoteTreatments),
      async ([providerId, userNotes]) => {
        const staffer = await resolveExactStaffer(
          providerId,
          translationMap,
          data.staff
        );
        if (!staffer) {
          throw new Error(`Failed to resolve practitioner code: ${providerId}`);
        }

        const dateGroupedNotes = groupBy(userNotes, (note) =>
          this.getNoteDate(note)
        );
        Object.values(dateGroupedNotes).map((notes) => {
          const content = this._treatmentsToNoteContent(
            notes,
            data.sourceItemCodes,
            data.itemCodeToTreatments
          );
          const noteDate = this.getNoteDate(notes[0]);
          const noteTimestamp = this.getNoteTimestamp(notes[0], timezone);
          clinicalNotes.push({
            sourceIdentifier: `${notes[0].data.patient_id}-${providerId}-${noteDate}`,
            ...ClinicalNote.init({
              owner: stafferToNamedDoc(staffer),
              content: initVersionedSchema(content),
              recordDate: noteDate ?? toISODate(toTimestamp()),
              createdAt: noteTimestamp,
              updatedAt: noteTimestamp,
              immutable: true,
            }),
          });
        });
      }
    );

    const userGroupedBpeScores = groupBy(
      bpeScores,
      (record) => record.data.user_code ?? '0'
    );

    await resolveSequentially(
      Object.entries(userGroupedBpeScores),
      async ([providerId, userScores]) => {
        const staffer = await resolveExactStaffer(
          providerId,
          translationMap,
          data.staff
        );
        if (!staffer) {
          throw new Error(
            `Failed to resolve BPE practitioner code: ${providerId}`
          );
        }

        userScores.map((score) => {
          const scoreParts = score.data.scores.split('\r\n');
          const formattedScores = `${scoreParts
            .slice(0, 3)
            .join(' ')}\n ${scoreParts.slice(3).join(' ')}\n\n`;
          const content = [
            toTextContent(formattedScores),
            toTextContent(`${score.data.notes || ' '}\n\n`),
          ];

          const scoreTimestamp = toTimestamp(
            toMomentTz(score.record.translations.date, timezone)
          );
          const noteDate = toISODate(scoreTimestamp);

          clinicalNotes.push({
            sourceIdentifier: `${score.data.patient_id}-${providerId}-${noteDate}-${PATIENT_BPE_RESOURCE_TYPE}`,
            ...ClinicalNote.init({
              owner: stafferToNamedDoc(staffer),
              content: initVersionedSchema(content),
              recordDate: noteDate,
              createdAt: scoreTimestamp,
              updatedAt: scoreTimestamp,
              immutable: true,
            }),
          });
        });
      }
    );

    if (pathologyNotes.length) {
      const staffer = await resolveExactStaffer(
        '0',
        translationMap,
        data.staff
      );
      if (!staffer) {
        throw new Error(
          'Failed to resolve default practitioner for pathology notes'
        );
      }

      for (const pathologyNote of pathologyNotes) {
        const noteTimestamp = pathologyNote.record.translations.date;
        const noteDate = toISODate(noteTimestamp);
        const content = [toTextContent(`${pathologyNote.data.note}\n\n`)];

        clinicalNotes.push({
          sourceIdentifier: `${pathologyNote.data.patient_id}-${pathologyNote.data.id}-${PATIENT_PATHOLOGY_NOTE_RESOURCE_TYPE}`,
          ...ClinicalNote.init({
            owner: stafferToNamedDoc(staffer),
            content: initVersionedSchema(content),
            recordDate: noteDate,
            createdAt: noteTimestamp,
            updatedAt: noteTimestamp,
            immutable: true,
          }),
        });
      }
    }

    return clinicalNotes;
  }
}
